import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import React, { Fragment } from "react";
import { toast } from "react-hot-toast";
import { DialogEnum, useDialog } from "../../contexts/DialogContext";
import {
  ChangePasswordFormValues,
  useChangePasswordForm,
} from "../../forms/auth/change-password-form";
import { CognitoService } from "../../services/cognito-service";

export default function ChangePasswordDialog() {
  const dialog = useDialog();
  const { register, handleSubmit, formState, reset } = useChangePasswordForm();

  const onSubmit = async (values: ChangePasswordFormValues) => {
    await toast.promise(
      CognitoService.changePassword(values.oldPassword, values.newPassword),
      {
        loading: "Saving...",
        success: () => {
          dialog.pop();
          return <b>Successful password change!</b>;
        },
        error: (e) => {
          return e?.response?.data
            ? e.response.data
            : e.message ?? "An Error Occurred.";
        },
      },
      { duration: 5000 }
    );
  };

  return (
    <Transition
      appear
      show={dialog.isOpened(DialogEnum.CHANGE_PASSWORD)}
      afterLeave={() =>
        reset({ oldPassword: "", newPassword: "", repeatNewPassword: "" })
      }
      as={Fragment}
    >
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={dialog.pop}
      >
        <div className="min-h-screen text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-700"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 transition-opacity bg-black bg-opacity-30" />
          </Transition.Child>

          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-md p-10 overflow-hidden text-left align-middle transition-all transform bg-white rounded-md shadow-xl">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Dialog.Title className="text-2xl font-semibold tracking-wide uppercase text-primary-blue">
                  Change password
                </Dialog.Title>
                <div className="mt-4">
                  <p className="text-sm text-gray-500">
                    Use the form below to change the password for your account
                  </p>
                </div>

                <div className="mt-5 flex flex-col gap-2.5">
                  <input
                    {...register("oldPassword")}
                    name="oldPassword"
                    type="password"
                    placeholder="Old Password"
                    autoComplete="off"
                    className={classNames(
                      "w-full bg-white block px-[15px] py-[12px] border border-primary-blue rounded-md focus:outline-none focus:ring-1 focus:ring-primary-blue",
                      {
                        "border-red-500 text-red-600 focus:border-red-500 focus:ring-red-500":
                          !!formState?.errors?.oldPassword,
                      }
                    )}
                  />
                  <div className="-mt-1 text-sm text-red-500">
                    {formState?.errors?.oldPassword?.message}
                  </div>
                  <input
                    {...register("newPassword")}
                    name="newPassword"
                    type="password"
                    placeholder="New Password"
                    autoComplete="off"
                    className={classNames(
                      "w-full bg-white block px-[15px] py-[12px] border border-primary-blue rounded-md focus:outline-none focus:ring-1 focus:ring-primary-blue",
                      {
                        "border-red-500 text-red-600 focus:border-red-500 focus:ring-red-500":
                          !!formState?.errors?.newPassword,
                      }
                    )}
                  />
                  <div className="-mt-1 text-sm text-red-500">
                    {formState?.errors?.newPassword?.message}
                  </div>
                  <input
                    {...register("repeatNewPassword")}
                    name="repeatNewPassword"
                    type="password"
                    placeholder="Repeat New Password"
                    autoComplete="off"
                    className={classNames(
                      "w-full bg-white block px-[15px] py-[12px] border border-primary-blue rounded-md focus:outline-none focus:ring-1 focus:ring-primary-blue",
                      {
                        "border-red-500 text-red-600 focus:border-red-500 focus:ring-red-500":
                          !!formState?.errors?.repeatNewPassword,
                      }
                    )}
                  />
                  <div className="-mt-1 text-sm text-red-500">
                    {formState?.errors?.repeatNewPassword?.message}
                  </div>
                  <button
                    className="uppercase w-full inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 text-white border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    type="submit"
                  >
                    Change password
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
